/**
 * 1.3.1 Administration → Media portal → Settings → Reminders and expiry (Q2): the optional expiry N days after the due
 * date (portal_settings.expiry_days, PATCH /api/v1/admin/portal/settings {expiryDays}), and what the reminder loop
 * last sent (GET /api/v1/admin/portal/reminders). The reminder times themselves stay in the Settings form
 * (Settings.tsx, Q1's). Rendered by PortalSettings (Settings.tsx). Q2's file.
 */
import { useEffect, useState } from 'react';
import { BellRing, Save } from 'lucide-react';
import type { PortalSettingsDTO } from '@prism/contracts';
import { api, errorDetail } from '../../../api/client';
import { usePortalAdminRefresh, usePortalSettings } from '../../../api/hooks/portalAdmin';
import { usePortalReminders } from '../../../api/hooks/portalStatistics';
import { toast } from '../../../components/Toasts';
import { dateTime, relative } from '../../../lib/format';

export function PortalReminderSettings() {
  const s = usePortalSettings().data;
  const sent = usePortalReminders(10);
  const refresh = usePortalAdminRefresh();
  const [on, setOn] = useState(false);
  const [days, setDays] = useState('14');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!s) return;
    setOn(s.expiryDays !== null);
    if (s.expiryDays !== null) setDays(String(s.expiryDays));
  }, [s]);
  if (!s) return null;
  const n = Number(days);
  const daysOk = /^\d+$/.test(days.trim()) && n >= 1 && n <= 365;
  const next = on ? n : null;
  const dirty = next !== s.expiryDays;
  const save = async () => {
    setBusy(true);
    try {
      await api<PortalSettingsDTO>('/api/v1/admin/portal/settings', { method: 'PATCH', json: { expiryDays: next } });
      await refresh();
      toast(on ? `Deliveries now expire ${n} ${n === 1 ? 'day' : 'days'} after their due date` : 'Deliveries no longer expire');
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  const offsets = s.reminderOffsetsDays.length ? [...s.reminderOffsetsDays].sort((a, b) => b - a).join(' and ') : null;
  const items = sent.data?.items ?? [];
  return (
    <section className="panel-card portal-form portal-section" data-testid="portal-reminders">
      <h3><BellRing size={16} color="var(--accent-strong)" /> Reminders and expiry</h3>
      <p className="muted portal-lede">
        {offsets
          ? <>Suppliers get a reminder email {offsets} {s.reminderOffsetsDays.length === 1 && s.reminderOffsetsDays[0] === 1 ? 'day' : 'days'} before a delivery is due, while nothing has been submitted. Reviewers’ daily digest lists the deliveries past their due date.</>
          : <>No reminders are set (the reminder times above are empty). Reviewers’ daily digest lists the deliveries past their due date.</>}
      </p>
      <label className="stats-check" style={{ fontSize: 13 }}>
        <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} data-testid="portal-expiry-on" />
        Expire deliveries that are overdue
      </label>
      {on && (
        <div className="field-row">
          <div className="field">
            <label htmlFor="ps-expiry">Days after the due date</label>
            <input id="ps-expiry" className={`input ${daysOk ? '' : 'invalid'}`} inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} data-testid="portal-expiry-days" />
            <span className="help">1–365. An expired delivery takes no more files; the supplier sees it as expired. Give it a new due date to reopen it. A delivery waiting for review does not expire.</span>
          </div>
        </div>
      )}
      <div className="portal-save-row">
        <button className="btn primary" disabled={!dirty || (on && !daysOk) || busy} onClick={() => void save()} data-testid="portal-expiry-save"><Save size={14} /> Save expiry</button>
      </div>
      <h4 style={{ margin: '8px 0 0' }}>Reminders sent</h4>
      {sent.isLoading ? <p className="muted">Loading…</p> : !items.length ? <p className="faint" style={{ margin: 0 }} data-testid="portal-reminders-none">None yet.</p> : (
        <div className="scroll">
          <table className="table compact" data-testid="portal-reminders-sent">
            <thead><tr><th>Sent</th><th>Delivery</th><th>Company</th><th>Before</th><th>Due</th><th style={{ textAlign: 'right' }}>Emails</th></tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={`${r.assignmentId}:${r.offsetDays}:${r.dueAt}`}>
                  <td title={dateTime(r.sentAt)}>{relative(r.sentAt)}</td>
                  <td><span className="mono">{r.assetCode}</span> {r.title}</td>
                  <td>{r.company}</td>
                  <td>{r.offsetDays} {r.offsetDays === 1 ? 'day' : 'days'}</td>
                  <td>{dateTime(r.dueAt)}</td>
                  <td className="mono" style={{ textAlign: 'right' }} title={r.emails ? undefined : 'Nobody to send to, or email is not set up'}>{r.emails}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
